import { clsx } from 'clsx';
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Modal — the desk's centred dialog (deskkit.modal, §13.11).
 *
 * §13.15, owner-directed 9 Sep: the desk has exactly ONE side surface, the rail.
 * Every chrome menu — account, bell, search — is a centred modal, not a second
 * panel beside the rail: "i dont like the burger button expanding into another
 * side menu now there's 2." If a design instinct says "put it in a drawer", it
 * belongs here instead.
 *
 * Use a Modal for a task; use a Pane for the current selection (Law 4).
 *
 * Responsive: the kit fixes a pixel width because an artboard is fixed. Here
 * `width` is a maximum and the modal becomes a near-full-screen sheet on the
 * phone.
 */

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  sub?: ReactNode;
  children: ReactNode;
  foot?: ReactNode;
  /** Desk maximum in px. Ignored below the breakpoint. */
  width?: number;
  className?: string;
  /**
   * On a phone, fill the screen instead of floating — for a form the phone
   * board draws as its own screen ("Expenses ▸ New") rather than a dialog.
   */
  phoneScreen?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  sub,
  children,
  foot,
  width = 520,
  className,
  phoneScreen = false,
}: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Move focus into the dialog. The artboards never needed this — a canvas is
    // clicked, never tabbed through — so it is ours to add.
    ref.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  /* A DIALOG BELONGS TO THE SURFACE, NOT TO A COLUMN.
     The kit draws the scrim and the box `position: absolute`, which is right on
     an artboard — the board IS the viewport. In the app they resolve against
     the nearest positioned ancestor, so a modal opened from a page covered the
     content column and left the rail and the top bar lit, and one opened from
     inside a docked pane was pinned to the pane, ~70px left of every board.
     Four lanes wrote their own wrapper around this (reception's DeskModal, the
     shell's account menu, two more); the kit carries it now: the dialog is
     portalled to <body> in its own `.maie` island and positioned fixed. */
  return createPortal(
    <div className="maie desk kit-modal-dock">
      <div className="dscrim" onClick={onClose} role="presentation" />
      <div
        ref={ref}
        className={clsx('dmodal', phoneScreen && 'dmodal-phone', className)}
        style={{ width: `min(${width}px, calc(100vw - 32px))` }}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
      >
        <p className="t-title">{title}</p>
        {sub ? <p className="t-sec mut">{sub}</p> : null}
        {children}
        {foot ? (
          <div className="dmodal-foot" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            {foot}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/**
 * RailMenu — the rail-foot account menu (deskkit.rail_menu).
 *
 * §13.3: built ONCE in the kit and inherited by all nine canvases. Five roles
 * specced it independently, which is exactly how five slightly different
 * account menus get shipped.
 *
 * It is a centred Modal, never a panel beside the rail — see the note on Modal.
 */
export interface RailMenuItem {
  key: string;
  label: ReactNode;
  onSelect: () => void;
}

const DEFAULT_ITEMS = (on: (k: string) => void): RailMenuItem[] => [
  { key: 'account', label: 'Account — devices · notifications · signature', onSelect: () => on('account') },
  { key: 'appearance', label: 'Appearance — EN / AR · text size · contrast · motion', onSelect: () => on('appearance') },
  { key: 'feedback', label: 'Feedback', onSelect: () => on('feedback') },
  { key: 'branch', label: 'Switch branch', onSelect: () => on('branch') },
  { key: 'signout', label: 'Sign out', onSelect: () => on('signout') },
];

export function RailMenu({
  open,
  onClose,
  items,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  /** Override only when a role genuinely differs — the point is that they don't. */
  items?: RailMenuItem[];
  onSelect?: (key: string) => void;
}) {
  const rows = items ?? DEFAULT_ITEMS(onSelect ?? (() => {}));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Account"
      sub="one human, one login — this device"
      width={460}
      foot={
        <button type="button" className="btn btn-outline" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="dmenu" style={{ position: 'static', boxShadow: 'none', border: 'none', minWidth: 0, padding: '10px 0 0' }}>
        {rows.map((item) => (
          <button key={item.key} type="button" className="mi" onClick={item.onSelect}>
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </Modal>
  );
}
